"use client";

import { useTranslations } from "next-intl";

import {
  OVERRIDE_MONTHS,
  PRICING_RANGES,
} from "@/lib/pricing-calculator/defaults";
import type { MonthSummary } from "@/lib/pricing-calculator/model";
import { cn } from "@/lib/utils";

import { useCalculatorEnvironment } from "./context";
import { NumberInput } from "./controls";

interface SeasonalityEditorProps {
  months: readonly MonthSummary[];
  overrides: Readonly<Record<number, number>>;
  onChange: (overrides: Record<number, number>) => void;
}

/**
 * Twelve editable funding-months with a bar sparkline of the resulting
 * revenue shape. Typed values become overrides; the sparkline redraws live.
 */
export function SeasonalityEditor({
  months,
  overrides,
  onChange,
}: SeasonalityEditorProps) {
  const t = useTranslations("pages.pricing.calculator.inputs");
  const { format, currencySymbol } = useCalculatorEnvironment();

  const visible = months.slice(0, OVERRIDE_MONTHS);
  const peak = Math.max(1, ...visible.map((month) => month.revenue));
  const hasOverrides = Object.keys(overrides).length > 0;

  const setMonth = (index: number, value: number) => {
    const next: Record<number, number> = { ...overrides, [index]: value };
    onChange(next);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <p className="text-ds-body-xs text-grey-500">
          {t("seasonalityHint", { count: OVERRIDE_MONTHS })}
        </p>
        <button
          type="button"
          onClick={() => onChange({})}
          disabled={!hasOverrides}
          className="text-ds-label-lg text-pulse-700 disabled:text-grey-400 shrink-0 font-medium underline-offset-4 hover:underline disabled:no-underline"
        >
          {t("seasonalityReset")}
        </button>
      </div>

      <div aria-hidden className="grid h-16 grid-cols-12 items-end gap-1 px-1">
        {visible.map((month, index) => {
          const overridden = Object.hasOwn(overrides, index);
          const height = Math.max(4, (month.revenue / peak) * 100);
          return (
            <div
              key={month.month}
              className={cn(
                "rounded-ds-xs transition-[height,background-color] duration-200 ease-out motion-reduce:transition-none",
                overridden ? "bg-pulse-700" : "bg-pulse-200"
              )}
              style={{ height: `${height}%` }}
            />
          );
        })}
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {visible.map((month, index) => {
          const id = `pricing-month-${index + 1}`;
          const overridden = Object.hasOwn(overrides, index);
          return (
            <div key={month.month} className="flex flex-col gap-1">
              <label
                htmlFor={id}
                className="text-ds-label-md text-grey-600 flex items-baseline justify-between gap-2"
              >
                <span>{t("seasonalityMonth", { number: index + 1 })}</span>
                <span className="text-grey-400 tabular-nums">
                  {format.monthLabel(month.start)}
                </span>
              </label>
              <NumberInput
                id={id}
                value={Math.round(month.revenue)}
                onChange={(value) => setMonth(index, value)}
                min={PRICING_RANGES.revenueOverride.min}
                max={PRICING_RANGES.revenueOverride.max}
                prefix={currencySymbol}
                size="sm"
                inputClassName={cn(
                  "h-10 text-right",
                  overridden
                    ? "text-grey-950 border-pulse-300"
                    : "text-grey-600"
                )}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
